/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import {
  navigationActions,
  parseQuery,
  RouteState,
  Stack,
  Stacks,
} from 'careand-redux/slices/ui/navigation';
import { useAppSelector } from 'careand-redux/store';
import React, { createContext, ReactElement, useContext } from 'react';
import { matchPath } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import './NavStack.scss';
import ErrorBoundary from 'components/Utils/ErrorBoundary';
import { ScrollRestorationContext } from 'services/ScrollRestoration';
import PatientError from 'components/Utils/PatientError';
import config from 'config';
import _ from 'lodash';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { deepLinkActions } from '../../careand-redux/slices/deep-link';
import MiscUtils from '../../services/MiscUtils';

interface RouteData {
  element: ReactElement | null;
  exact?: boolean;
  authenticated?: boolean;
  key?: string;
}

interface NavStackProps {
  stack: Stack;
  routes: Record<string, RouteData | undefined>;
  debugName?: string;
  authenticateAll?: boolean;
}

export type NavStackRoutes = NavStackProps['routes'];

const PathParams = createContext<unknown>({});
export const usePathParams = () => useContext(PathParams);

const QueryParams = createContext<unknown>({});
export const useQueryParams = () => useContext(QueryParams);

export const NavStack = ({ routes, ...props }: NavStackProps) => {
  const dispatch = useAppDispatch();
  const { idx, stack, lastAction, state } = useAppSelector(
    (state) => state.navigation.stacks[props.stack],
  );
  const isLoggedIn = useAppSelector((state) => state.authentication.loggedIn);
  const loginRole = useAppSelector((state) => state.authentication.role);
  const isPatient = !loginRole || loginRole === 'patient';
  const masterNav = useNavStack(Stacks.Master);
  const scroll = useContext(ScrollRestorationContext);

  let route: string | undefined = undefined;
  let routeState: RouteState | undefined = undefined;
  if (idx >= 0 && idx < stack.length) {
    route = stack[idx];
    routeState = state?.[idx];
  }

  if (!route) {
    throw 'ERROR: Unknown route';
  }

  scroll.current.read = routeState?.scroll ?? 0;

  let routeData: RouteData | undefined;
  let pathParams: {} = {};
  let queryParams: {} = {};

  for (const path in routes) {
    const [pathname] = route.split('?');
    const match = matchPath(pathname ?? '', {
      path,
      exact: routes[path]?.exact,
    });
    if (match != null) {
      pathParams = match.params;
      routeData = routes[path];
      queryParams = parseQuery(route);
      break;
    }
  }

  const isAuthenticatedRoute = props.authenticateAll === true || routeData?.authenticated !== false;

  if (props.stack === Stacks.Master) {
    dispatch(navigationActions.setAuthenticated(isAuthenticatedRoute));
  }

  if (isAuthenticatedRoute && !isLoggedIn) {
    const here = window.location.pathname + window.location.search;
    const role: 'patient' | 'physician' = loginRole === 'patient' ? 'patient' : 'physician';
    // create pending deep link
    if (here && here !== '/' && !here.startsWith('/landing') && !here.startsWith('/login')) {
      dispatch(deepLinkActions.setIntent({ url: here, role }));
    }
    if (isPatient) {
      masterNav.pushAsFirst('/landing');
    } else {
      masterNav.pushAsFirst('/login');
    }
  }

  const timeout = isPatient && config.ENVIRONMENT !== 'test' ? 400 : 0;
  const className = lastAction ?? 'replace';

  const navStackClass = `nav-stack stack-${props.stack.toLowerCase()}`;

  const routeKey = routeData?.key || _.random(1, 5_000_000, false).toString();

  const element = routeData?.element ?? null; //useMemo(() => routeData?.element, [routeKey])

  return isPatient ? (
    <Transition
      transitionGroup={{
        className: navStackClass,
        childFactory: (child) => React.cloneElement(child, { classNames: className }),
      }}
      cssTransition={{ key: route, timeout, classNames: className }}
    >
      <PatientContent element={element} pathParams={pathParams} queryParams={queryParams} />
    </Transition>
  ) : (
    <StaffContent element={element} pathParams={pathParams} queryParams={queryParams} />
  );
};

function PatientContent(props: {
  element?: ReactElement | null;
  pathParams: unknown;
  queryParams: unknown;
}) {
  return (
    <PathParams.Provider value={props.pathParams ?? {}}>
      <QueryParams.Provider value={props.queryParams}>
        <ErrorBoundary content={<PatientError />}>{props.element ?? null}</ErrorBoundary>
      </QueryParams.Provider>
    </PathParams.Provider>
  );
}

function StaffContent(props: {
  element?: ReactElement | null;
  pathParams: unknown;
  queryParams: unknown;
}) {
  return (
    <PathParams.Provider value={props.pathParams ?? {}}>
      <QueryParams.Provider value={props.queryParams}>{props.element ?? null}</QueryParams.Provider>
    </PathParams.Provider>
  );
}

function Transition(props: {
  transitionGroup: { className: string; childFactory: (child: ReactElement) => ReactElement };
  cssTransition: { key: string; timeout: number; classNames: string };
  children: ReactElement;
}) {
  return (
    <TransitionGroup {...props.transitionGroup}>
      <CSSTransition {...props.cssTransition}>{props.children}</CSSTransition>
    </TransitionGroup>
  );
}
